import {
  useEffect,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Box,
  Button,
  Input,
  Spinner,
  Text,
  Textarea
} from '@chakra-ui/react'
import {
  chatCredentialsSelectors,
  getNotificationMessage,
  setChatSectionPage,
  usersSelectors
} from 'store'
import {
  changeRecoveryKey,
  restoreWithRecoveryKey,
  setupRecoveryKey,
  resetRecoveryKey
} from 'store/chat/services'
import {
  setNeedsToInputRecoveryKey,
  setRecoveryKey,
  setRestoreWithRecoveryKeyStatus
} from 'store/chat/slice'
import {
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import { COLORS } from 'utils/styles'

export const RecoveryKey = () => {
  const dispatch = useDispatch()

  const [inputRecoveryKey, setInputRecoveryKey] = useState('')
  const [inputPW, setInputPW] = useState('')
  const [showContents, setShowContents] = useState(false)
  const [doRDI, setDoRDI] = useState(false)

  const {
    checkIfHasRecoveryKeyStatus,
    checkIfNeedsRecoveryKeyInputStatus,
    hasRecoveryKey,
    needsToInputRecoveryKey,
    recoveryKey,
    resetRecoveryKeyStatus,
    restoreWithRecoveryKeyStatus,
    changeRecoveryKeyStatus,
    setupRecoveryKeyStatus
  } = useSelector(chatCredentialsSelectors)

  const { chatSectionPage } = useSelector(usersSelectors)

  const hasAnOnGoingAction = checkIfHasRecoveryKeyStatus === PENDING
    || changeRecoveryKeyStatus === PENDING
    || setupRecoveryKeyStatus === PENDING
    || restoreWithRecoveryKeyStatus === PENDING
    || resetRecoveryKeyStatus === PENDING

  const handleBtnText = () => {
    if (
      hasAnOnGoingAction
      && (
        changeRecoveryKeyStatus === PENDING
        || setupRecoveryKeyStatus === PENDING
      )
    ) {
      return <Spinner size="md" />
    }

    if (hasRecoveryKey) {
      return 'Change Recovery Key'
    }

    return 'Get Recovery Key'
  }

  const handleTextAreaCopy = async () => {
    try {
      await navigator.clipboard.writeText(recoveryKey)

      dispatch(
        getNotificationMessage({
          type: SUCCEEDED,
          message: 'Copied to clipboard',
          isEditProfile: false
        })
      )
    } catch (err) {
      console.error('Copy failed', err)
    }
  }

  useEffect(() => {
    if (restoreWithRecoveryKeyStatus === SUCCEEDED) {
      dispatch(setRestoreWithRecoveryKeyStatus(0))
      setInputRecoveryKey('')
      dispatch(setNeedsToInputRecoveryKey(false))

      if (chatSectionPage.at(-1) === 'settings') {
        dispatch(setChatSectionPage(null))
      }
    }
  }, [restoreWithRecoveryKeyStatus])

  useEffect(() => {
    if (
      checkIfNeedsRecoveryKeyInputStatus === SUCCEEDED
      || checkIfNeedsRecoveryKeyInputStatus === REJECTED
    ) {
      setShowContents(true)
    }
  }, [checkIfNeedsRecoveryKeyInputStatus])

  useEffect(() => {
    dispatch(setRecoveryKey(''))
  }, [])

  return (
    <Box
      display={showContents ? 'flex' : 'none'}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
      gap={4}
    >
      {
        needsToInputRecoveryKey
        && !doRDI
          ? (
            <>
              <Text
                alignSelf="flex-start"
                paddingLeft="5%"
                color={COLORS.white}
              >
                Verify Recovery Key
              </Text>
              <Input
                width="90%"
                color={COLORS.white}
                placeholder="Input your recovery key here"
                value={inputRecoveryKey ?? ''}
                onChange={(e) => setInputRecoveryKey(e.target.value)}
              />
              <Button
                background={hasAnOnGoingAction ? COLORS.gray : COLORS.gold}
                color={COLORS.white}
                _hover={{ background: hasAnOnGoingAction ? COLORS.gray : COLORS.gold_hover }}
                cursor="pointer"
                pointerEvents={hasAnOnGoingAction ? 'none' : 'auto'}
                onClick={() => {
                  if (!hasAnOnGoingAction) {
                    dispatch(restoreWithRecoveryKey({ recoveryKey: inputRecoveryKey }))
                  }
                }}
              >
                {
                  hasAnOnGoingAction
                  && restoreWithRecoveryKeyStatus === PENDING
                    ? <Spinner size="md" />
                    : 'Verify'
                }
              </Button>
            </>
          )
          : ''
      }

      {
        !needsToInputRecoveryKey
        && !doRDI
          ? (
            <>
              <Text
                alignSelf="flex-start"
                paddingLeft="5%"
                color={COLORS.white}
              >
                Recovery Key
                <Text
                  color="yellow"
                  fontSize="14px"
                >
                  Store this on a safe place
                </Text>
              </Text>
              <Textarea
                width="90%"
                color={COLORS.white}
                value={recoveryKey}
                placeholder="Your recovery key will be displayed here once it gets generated"
                cursor={recoveryKey ? 'pointer' : 'default'}
                onClick={() => {
                  if (recoveryKey) {
                    handleTextAreaCopy()
                  }
                }}
                readOnly
              />

              <Button
                background={hasAnOnGoingAction ? COLORS.gray : COLORS.gold}
                color={COLORS.white}
                _hover={{ background: hasAnOnGoingAction ? COLORS.gray : COLORS.gold_hover }}
                cursor="pointer"
                pointerEvents={hasAnOnGoingAction ? 'none' : 'auto'}
                onClick={() => {
                  if (!hasAnOnGoingAction) {
                    if (hasRecoveryKey) {
                      dispatch(setRecoveryKey(''))
                      dispatch(changeRecoveryKey())
                    } else {
                      dispatch(setupRecoveryKey())
                    }
                  }
                }}
              >
                {handleBtnText()}
              </Button>
            </>
          )
          : ''
      }

      {
        doRDI
          ? (
            <>
              <Text
                alignSelf="flex-start"
                paddingLeft="5%"
                color={COLORS.white}
              >
                Recovery Key
                <Text
                  color="yellow"
                  fontSize="14px"
                >
                  Store this on a safe place
                </Text>
              </Text>

              <Textarea
                width="90%"
                color={COLORS.white}
                value={recoveryKey}
                placeholder="Your recovery key will be displayed here once it gets reset"
                cursor={recoveryKey ? 'pointer' : 'default'}
                onClick={() => {
                  if (recoveryKey) {
                    handleTextAreaCopy()
                  }
                }}
                readOnly
              />

              <Text
                display={
                  resetRecoveryKeyStatus === SUCCEEDED
                  && recoveryKey
                    ? 'none'
                    : 'block'
                }
                alignSelf="flex-start"
                paddingLeft="5%"
                color={COLORS.white}
              >
                Reset Digital Identity
                <Text
                  color="yellow"
                  fontSize="14px"
                >
                  Doing so may result to lost of old messages
                </Text>
              </Text>

              <Input
                display={
                  resetRecoveryKeyStatus === SUCCEEDED
                  && recoveryKey
                    ? 'none'
                    : 'flex'
                }
                type="password"
                width="90%"
                color={COLORS.white}
                placeholder="Password"
                value={inputPW ?? ''}
                onChange={(e) => setInputPW(e.target.value)}
              />

              <Box
                display="flex"
                justifyContent="center"
                gap={4}
              >
                <Button
                  display={
                    resetRecoveryKeyStatus === SUCCEEDED
                    && recoveryKey
                      ? 'flex'
                      : 'none'
                  }
                  background={hasAnOnGoingAction ? COLORS.gray : COLORS.gold}
                  color={COLORS.white}
                  _hover={{ background: hasAnOnGoingAction ? COLORS.gray : COLORS.gold_hover }}
                  cursor="pointer"
                  pointerEvents={hasAnOnGoingAction ? 'none' : 'auto'}
                  onClick={() => {
                    setInputPW('')
                    dispatch(setRecoveryKey(''))
                    dispatch(setChatSectionPage(null))
                  }}
                >
                  Back
                </Button>

                <Button
                  display={
                    resetRecoveryKeyStatus === SUCCEEDED
                    && recoveryKey
                      ? 'none'
                      : 'flex'
                  }
                  background={hasAnOnGoingAction ? COLORS.gray : COLORS.gold}
                  color={COLORS.white}
                  _hover={{ background: hasAnOnGoingAction ? COLORS.gray : COLORS.gold_hover }}
                  cursor="pointer"
                  pointerEvents={hasAnOnGoingAction ? 'none' : 'auto'}
                  onClick={() => {
                    setInputPW('')
                    dispatch(setRecoveryKey(''))
                    setDoRDI(false)
                  }}
                >
                  Cancel
                </Button>

                <Button
                  display={
                    resetRecoveryKeyStatus === SUCCEEDED
                    && recoveryKey
                      ? 'none'
                      : 'flex'
                  }
                  background={hasAnOnGoingAction ? COLORS.gray : COLORS.gold}
                  color={COLORS.white}
                  _hover={{ background: hasAnOnGoingAction ? COLORS.gray : COLORS.gold_hover }}
                  cursor="pointer"
                  pointerEvents={hasAnOnGoingAction ? 'none' : 'auto'}
                  onClick={() => {
                    if (!hasAnOnGoingAction) {
                      dispatch(setRecoveryKey(''))
                      setInputPW('')
                      dispatch(resetRecoveryKey({ password: inputPW }))
                    }
                  }}
                >
                  {
                    hasAnOnGoingAction
                    && resetRecoveryKeyStatus === PENDING
                      ? <Spinner size="md" />
                      : 'Reset'
                  }
                </Button>
              </Box>
            </>
          )
          : ''
      }

      <Text
        display={doRDI ? 'none' : 'block'}
        color={COLORS.red}
        textDecoration="underline"
        cursor="pointer"
        position="absolute"
        bottom="28px"
        left="50%"
        transform="translateX(-50%)"
        onClick={() => {
          dispatch(setRecoveryKey(''))
          setDoRDI(true)
        }}
      >
        Reset Digital Identity
      </Text>
    </Box>
  )
}
